import React from "react"
import { connect } from "react-redux"
import { Route, Routes } from "react-router-dom"
import { getCurrentUser, getBaseUrl } from "../reducers"
import SettingsPage from "./Onboarding/SettingsPage"
import Dashboard from "./Dashboard"
import PulseCheckDashboard from "./Dashboard/Pulse/PulseCheckDashboard"
import ProtectedRoute from "./ProtectedRoute"
import DemographicsForm from "./Dashboard/DemographicsForm"
import DimensionDetailPage from "./Dashboard/DimensionDetailPage"
import {
  BackendDashboard,
  BackendStatsDashboard,
  BackendTemplatesDash,
  BackendUsersDashboard,
  CreateOrgPage,
  CreateTemplatePage,
  OrganisationsDashboard,
  OrgDetailPage,
  ProgramDetailPage,
  ProgramPreviewPage,
  TemplateDetailPage,
  TemplatePreviewPage,
  UnitDetailPage,
  UserDetailPage
} from "./Backend"
import SurveyFormPage from "./Survey/SurveyFormPage"
import SurveyIntro from "./Survey/SurveyIntro"
import SurveyOverviewPage from "./Dashboard/SurveyOverviewPage"
import SurveyEnd from "./Survey/SurveyEnd"
import AboutTenDwc from "./Dashboard/AboutTenDwc"
import LinkingCodePage from "./Dashboard/LinkingCodePage"
import EmailVerification from "./Onboarding/EmailVerification"
import PulseCheckFormPage from "./Survey/Pulse/PulseCheckFormPage"
import PulseCheckEnd from "./Survey/Pulse/PulseCheckEnd"
import { MentalSkillsIntro, MentalSkillsEnd, MentalSkillsFormPage } from "./Survey/MentalSkills"
import MentalSkillsDashboard from "./Dashboard/MentalSkills/MentalSkillsDashboard"
import AboutMentalSkills from "./Dashboard/MentalSkills/AboutMentalSkills"
import MatchfitDashboard from "./Dashboard/Matchfit/MatchfitDashboard"
import AboutMatchfit from "./Dashboard/Matchfit/AboutMatchfit"
import CategoryDetailPage from "./Dashboard/MentalSkills/CategoryDetailPage"
import MatchfitCategoryDetailPage from "./Dashboard/Matchfit/MatchfitCategoryDetailPage"
import {
  MatchfitIntro, MatchfitFormPage, MatchfitEnd, MatchfitSimple
} from "./Survey/Matchfit"
import { LiveLifeIntro, LiveLifeFormPage } from "./Survey/Livelife"
import featureFlags from "../util/featureFlags"
import { pageTitle } from "../util"
import BackendExportsDashboard from "./Backend/BackendExportsDashboard"
import BackendTagsDashboard from "./Backend/BackendTagsDashboard"
import BackendActivityLogsDashboard from "./Backend/BackendActivityLogsDashboard"
import {
  BurnoutProofFormPage, BurnoutProofIntro
} from "./Survey/BurnoutProof"
import BurnoutProofDashboard from "./Dashboard/BurnoutProof/BurnoutProofDashboard"
import AboutBurnoutProof from "./Dashboard/BurnoutProof/AboutBurnoutProof"
import LiveLifeDashboard from "./Dashboard/LiveLife/LiveLifeDashboard"
import LiveLifeV2Dashboard from "./Dashboard/LiveLife/LiveLifeV2Dashboard"
import AboutLiveLife from "./Dashboard/LiveLife/AboutLiveLife"

// One entry per page. `paths` lists every URL that renders that page — react-router
// v6+ dropped v5's array-of-paths support, so each path becomes its own <Route>.
// `title` is the page-specific half of the document title; omit it to fall back to
// the bare app name. `flag` gates the route behind featureFlags().
//
// object-curly-newline is disabled for the table only: one line per route keeps it
// scannable, which matters more here than the 4-property wrapping rule.
/* eslint-disable object-curly-newline */
const ROUTES = [
  // 10DWC DASHBOARD PAGES
  { paths: ["/ten-dwc-dashboard"], component: Dashboard, title: "10DWC Dashboard", redirectedRoute: "/login" },
  { paths: ["/about-10dwc"], component: AboutTenDwc, redirectedRoute: "/" },
  { paths: ["/dimension/:dimensionId"], component: DimensionDetailPage, title: "Dimension Detail Page", redirectedRoute: "/login" },

  // PULSE CHECK DASHBOARD PAGES
  { paths: ["/pulse-check-dashboard"], component: PulseCheckDashboard, title: "Pulse Check Dashboard", redirectedRoute: "/login" },

  // MENTAL SKILLS DASHBOARD PAGES
  { paths: ["/mental-skills-dashboard"], component: MentalSkillsDashboard, title: "Mental Skills Dashboard", redirectedRoute: "/login" },
  { paths: ["/category/:categoryId"], component: CategoryDetailPage, title: "Category Detail Page", redirectedRoute: "/login" },
  { paths: ["/about-mental-skills"], component: AboutMentalSkills, redirectedRoute: "/" },

  // MATCHFIT DASHBOARD PAGES
  { paths: ["/matchfit-dashboard"], component: MatchfitDashboard, title: "MatchFit Dashboard", redirectedRoute: "/login" },
  { paths: ["/matchfit-category/:categoryId"], component: MatchfitCategoryDetailPage, title: "MatchFit Category Detail Page", redirectedRoute: "/login" },
  { paths: ["/about-matchfit"], component: AboutMatchfit, redirectedRoute: "/" },

  // BURNOUT PROOF DASHBOARD PAGES
  { paths: ["/burnout-proof-dashboard"], component: BurnoutProofDashboard, title: "Burnout Proof Dashboard", redirectedRoute: "/login" },

  // LIVE LIFE DASHBOARD PAGES
  { paths: ["/livelife-dashboard"], component: LiveLifeDashboard, title: "PQ Index Dashboard", redirectedRoute: "/login" },
  { paths: ["/livelife-v2-dashboard"], component: LiveLifeV2Dashboard, title: "PQ Index Dashboard", redirectedRoute: "/login" },
  { paths: ["/about-burnout-proof"], component: AboutBurnoutProof, redirectedRoute: "/" },

  // SURVEY PAGES
  { paths: ["/", "/overview"], component: SurveyOverviewPage, title: "Overview", redirectedRoute: "/login" },
  { paths: ["/pulse-check/questions", "/pulse-check/intro"], component: PulseCheckFormPage, title: "Pulse Check Questions", redirectedRoute: "/" },
  { paths: ["/pulse-check/thank-you"], component: PulseCheckEnd, redirectedRoute: "/" },

  // MATCHFIT SURVEY PAGES
  { paths: ["/matchfit/intro", "/matchfit/questions"], component: MatchfitIntro, title: "Matchfit Calculator Intro", redirectedRoute: "/" },
  { paths: ["/matchfit/questions/:moduleId"], component: MatchfitFormPage, title: "Matchfit Calculator Questions", redirectedRoute: "/" },
  { paths: ["/matchfit/thank-you"], component: MatchfitEnd, redirectedRoute: "/" },
  { paths: ["/matchfit/simple"], component: MatchfitSimple, redirectedRoute: "/", flag: "STAGING" },

  // LIVELIFE SURVEY PAGES
  { paths: ["/livelife/intro"], component: LiveLifeIntro, title: "PQ Index Intro", redirectedRoute: "/" },
  { paths: ["/livelife/questions"], component: LiveLifeFormPage, title: "PQ Index Questions", redirectedRoute: "/" },
  { paths: ["/about-livelife"], component: AboutLiveLife, redirectedRoute: "/" },

  // 10DWC SURVEY PAGES
  { paths: ["/survey/intro"], component: SurveyIntro, title: "10DWC Survey Intro", redirectedRoute: "/" },
  { paths: ["/survey/questions"], component: SurveyFormPage, title: "10DWC Survey Questions", redirectedRoute: "/" },
  { paths: ["/survey/thank-you"], component: SurveyEnd, redirectedRoute: "/" },

  // MENTAL SKILLS SURVEY PAGES
  { paths: ["/mental-skills/intro"], component: MentalSkillsIntro, title: "Mental Skills Intro", redirectedRoute: "/" },
  { paths: ["/mental-skills/questions"], component: MentalSkillsFormPage, title: "Mental Skills Questions", redirectedRoute: "/" },
  { paths: ["/mental-skills/thank-you"], component: MentalSkillsEnd, redirectedRoute: "/" },

  // BURNOUT PROOF SURVEY PAGES
  { paths: ["/burnout-proof/intro"], component: BurnoutProofIntro, title: "Burnout Proof Intro", redirectedRoute: "/" },
  { paths: ["/burnout-proof/questions"], component: BurnoutProofFormPage, title: "Burnout Proof Questions", redirectedRoute: "/" },

  // USER/SETTINGS PAGES
  { paths: ["/settings"], component: SettingsPage, title: "Settings", redirectedRoute: "/" },
  { paths: ["/verify-email"], component: EmailVerification, title: "Verify Email", redirectedRoute: "/login" },
  { paths: ["/enrol"], component: LinkingCodePage, redirectedRoute: "/login" },
  { paths: ["/demographic/:rerouteId", "/demographic"], component: DemographicsForm, title: "Sociodemographic Questions", redirectedRoute: "/login" },

  // BACKEND DASHBOARD PAGES
  { paths: ["/backend"], component: BackendDashboard, title: "Backend Dashboard", redirectedRoute: "/" },
  { paths: ["/backend/users"], component: BackendUsersDashboard, title: "Backend Users Dashboard", redirectedRoute: "/" },
  { paths: ["/backend/users/:userId"], component: UserDetailPage, title: "Backend Users Dashboard", redirectedRoute: "/" },
  { paths: ["/backend/mis"], component: BackendStatsDashboard, title: "Backend MIS Stats", redirectedRoute: "/" },
  { paths: ["/backend/exports"], component: BackendExportsDashboard, title: "Backend MIS Stats", redirectedRoute: "/" },
  { paths: ["/backend/templates"], component: BackendTemplatesDash, title: "Backend Templates", redirectedRoute: "/" },
  { paths: ["/backend/create-unit"], component: CreateOrgPage, title: "Unit Create", redirectedRoute: "/" },
  { paths: ["/backend/create-template"], component: CreateTemplatePage, title: "Template Create", redirectedRoute: "/" },
  { paths: ["/backend/tags"], component: BackendTagsDashboard, title: "Tags", redirectedRoute: "/" },
  { paths: ["/backend/activity-logs"], component: BackendActivityLogsDashboard, title: "Activity Logs", redirectedRoute: "/" },
  { paths: ["/backend/templates/:templateId"], component: TemplateDetailPage, title: "Template Detail Page", redirectedRoute: "/" },
  { paths: ["/backend/templates/preview/:templateId"], component: TemplatePreviewPage, title: "Template Detail Preview Page", redirectedRoute: "/" },
  { paths: ["/backend/organisations"], component: OrganisationsDashboard, title: "Organisations", redirectedRoute: "/" },
  { paths: ["/backend/organisations/:organisationId"], component: OrgDetailPage, title: "Organisation Detail Page", redirectedRoute: "/" },
  { paths: ["/backend/units/:unitId"], component: UnitDetailPage, title: "Unit Detail Page", redirectedRoute: "/" },
  { paths: ["/backend/programs/preview/:programId"], component: ProgramPreviewPage, title: "Program Preview Page", redirectedRoute: "/" },
  { paths: ["/backend/programs/:programId"], component: ProgramDetailPage, title: "Program Detail Page", redirectedRoute: "/" }
]
/* eslint-enable object-curly-newline */

function Main({ currentUser, baseUrl }) {
  return (
    <Routes>
      {ROUTES
        .filter(({ flag }) => !flag || featureFlags(flag))
        .flatMap(({
          paths, component, title, redirectedRoute
        }) => paths.map(path => (
          <Route
            element={(
              <ProtectedRoute
                baseUrl={baseUrl}
                component={component}
                currentUser={currentUser}
                redirectedRoute={redirectedRoute}
                title={pageTitle(title)} />
            )}
            key={path}
            path={path} />
        )))}
    </Routes>
  )
}

const mapStateToProps = state => ({
  currentUser: getCurrentUser(state),
  baseUrl: getBaseUrl(state)
})

export default connect(mapStateToProps)(Main)
